/* 🔥 PhoenixTierModal — lore lookup for a Phoenix Ascension tier
 * ----------------------------------------------------------------
 * Opens when a player taps their tier banner on the honor leaderboard.
 * Fetches the single-tier detail (id + optional live progress card
 * for the viewer) and shows the full lore + honor range + next-tier peek.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PhoenixTierModal({ tierId, honor, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);

  useEffect(() => {
    if (!tierId && honor == null) return;
    const q = tierId ? `tier_id=${encodeURIComponent(tierId)}` : `honor=${encodeURIComponent(honor)}`;
    axios.get(`${API}/phoenix/tiers/lookup?${q}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Couldn't load tier."));
  }, [tierId, honor]);

  if (!tierId && honor == null) return null;
  const t = data?.tier;
  const p = data?.progress;

  return (
    <div
      data-testid="phoenix-tier-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xl rounded-2xl border-2 shadow-2xl overflow-hidden"
        style={{
          borderColor: t?.color || "#F59E0B",
          background: `radial-gradient(circle at 30% 20%, ${t?.color || "#F59E0B"}22, transparent 60%), #0A0806`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="phoenix-tier-close"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 hover:bg-black text-neutral-300 hover:text-white font-black"
        >×</button>

        {err && (
          <div className="p-8 text-center text-rose-300 text-sm">{err}</div>
        )}
        {!err && !t && (
          <div className="p-10 text-center text-neutral-500 italic">Loading tier lore…</div>
        )}
        {t && (
          <>
            <div
              className="px-6 pt-8 pb-5 border-b border-neutral-800/60"
              style={{ background: `linear-gradient(180deg, ${t.color}22, transparent)` }}
            >
              <div className="text-[10px] uppercase tracking-[0.3em] font-black mb-1"
                   style={{ color: t.color }}>
                Phoenix Ascension · Rank {t.rank} / 14
              </div>
              <div className="flex items-center gap-4">
                <div className="text-6xl leading-none drop-shadow-[0_0_20px_rgba(0,0,0,0.9)]"
                     style={{ textShadow: `0 0 24px ${t.color}` }}>
                  {t.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-black tracking-wide text-3xl md:text-4xl leading-tight"
                      style={{ fontFamily: "Anton, sans-serif", color: t.color }}>
                    {t.name}
                  </h2>
                  <p className="text-xs text-neutral-400 mt-1 italic">"{t.tagline}"</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-neutral-300 leading-relaxed">{t.lore}</p>

              <div className="flex flex-wrap gap-2 pt-2 border-t border-neutral-900">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold w-full">
                  Honor Range
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-neutral-400">🏅 {t.min_honor.toLocaleString()}</span>
                  <span className="text-neutral-600">→</span>
                  <span className="text-neutral-400">
                    {t.max_honor != null ? `${t.max_honor.toLocaleString()}` : "∞ (endgame)"}
                  </span>
                </div>
              </div>

              {p && p.next && (
                <div className="pt-3 border-t border-neutral-900">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2">
                    Your Ascension
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-neutral-400">
                      {p.honor.toLocaleString()}🏅
                    </span>
                    <span className="text-neutral-600">→</span>
                    <span className="font-bold" style={{ color: p.next.color }}>
                      {p.next.icon} {p.next.name}
                    </span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-neutral-900 overflow-hidden">
                    <div className="h-full transition-all duration-500"
                         style={{ width: `${p.pct_to_next}%`,
                                    background: `linear-gradient(90deg, ${t.color}, ${p.next.color})` }} />
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-1">
                    {p.honor_to_next.toLocaleString()} honor to next ascension
                  </div>
                </div>
              )}
              {p && !p.next && (
                <div className="pt-3 border-t border-neutral-900 text-center">
                  <div className="text-xs font-black tracking-wider" style={{ color: t.color }}>
                    🌌 YOU HAVE REACHED THE PRIMORDIAL 🌌
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
